import { type FormEvent, type JSX, useCallback, useEffect, useState } from 'react';

import {
  type Kennzahlenkatalog,
  type Zielmerkmale,
  type Zielwert,
  type Zielwerteingabe,
  wertText,
  zielmerkmale,
  zielwertLoeschen,
  zielwertSpeichern,
  zielwerte as holeZielwerte,
} from '../api/berichte';
import { ApiFehler } from '../api/fehler';
import { Meldung } from '../komponenten/Meldung';
import { Rueckfrage } from '../komponenten/Rueckfrage';
import { bereiteKennzahlen } from './auswahl';
import { einheitszeichen } from './Ergebnis';

/**
 * DIE PFLEGE DER ZIELWERTE (AUS-12, Auftrag #29).
 *
 * Je Kennzahl und Periode (Monat, Quartal, Jahr), wahlweise je Betreuer oder
 * Kundengruppe, mit der Toleranz der Ampel. Speichern ersetzt ein Ziel mit
 * demselben Schlüssel — ein zweites Umsatzziel für September gibt es nicht.
 *
 * Sehen darf die Ziele, wer Berichte sieht (nur die seiner Kennzahlen);
 * pflegen nur mit `auswertung:zielwerte_pflegen` (AeA-43). Ohne das Recht
 * steht die Liste da, das Formular nicht — und die Maske sagt es.
 */
const PERIODENNAMEN = { monat: 'Monat', quartal: 'Quartal', jahr: 'Jahr' } as const;
const DIMENSIONSNAMEN = { gesamt: 'Gesamt', betreuer: 'Betreuer', kundengruppe: 'Kundengruppe' } as const;

function periodeText(z: Pick<Zielwert, 'periodenart' | 'periode'>): string {
  if (z.periodenart === 'monat') return `${z.periode.slice(5, 7)}/${z.periode.slice(0, 4)}`;
  if (z.periodenart === 'quartal') return `${z.periode.slice(5)} ${z.periode.slice(0, 4)}`;
  return z.periode;
}

export function Zielwerte({
  katalog,
  darfPflegen,
}: {
  readonly katalog: Kennzahlenkatalog;
  readonly darfPflegen: boolean;
}): JSX.Element {
  const [liste, setzeListe] = useState<readonly Zielwert[] | undefined>(undefined);
  const [merkmale, setzeMerkmale] = useState<Zielmerkmale>({ betreuer: [], kundengruppen: [] });
  const [fehler, setzeFehler] = useState<ApiFehler | undefined>(undefined);
  const [hinweis, setzeHinweis] = useState<string | undefined>(undefined);
  /** CRM-104: das Ziel, dessen Löschen im Pop-up nachfragt. */
  const [zuLoeschen, setzeZuLoeschen] = useState<Zielwert | null>(null);
  const jahr = new Date().getFullYear();
  const [eingabe, setzeEingabe] = useState<Zielwerteingabe>({
    // Auftrag CRM-44: Ziele nur fuer Kennzahlen, die rechnen.
    kennzahl: bereiteKennzahlen(katalog.kennzahlen)[0]?.kennung ?? '',
    periodenart: 'monat',
    periode: `${String(jahr)}-${String(new Date().getMonth() + 1).padStart(2, '0')}`,
    dimension: 'gesamt',
    dimensionswert: '',
    wert: '',
    toleranz: '5',
  });

  const laden = useCallback(async (): Promise<void> => {
    try {
      setzeListe(await holeZielwerte());
      if (darfPflegen) setzeMerkmale(await zielmerkmale());
    } catch (u) {
      if (u instanceof ApiFehler) setzeFehler(u);
      else throw u;
    }
  }, [darfPflegen]);

  useEffect(() => {
    void laden();
  }, [laden]);

  const name = (kennung: string): string => katalog.kennzahlen.find((k) => k.kennung === kennung)?.name ?? kennung;
  const einheit = (kennung: string) => katalog.kennzahlen.find((k) => k.kennung === kennung)?.einheit ?? 'Anzahl';
  const fuer = (z: Zielwert): string =>
    z.dimension === 'gesamt'
      ? 'Gesamt'
      : z.dimension === 'betreuer'
        ? `Betreuer ${merkmale.betreuer.find((b) => b.id === z.dimensionswert)?.name ?? z.dimensionswert}`
        : `Kundengruppe ${z.dimensionswert}`;

  function setze(teil: Partial<Zielwerteingabe>): void {
    setzeHinweis(undefined);
    setzeEingabe((e) => ({ ...e, ...teil }));
  }

  function periodenart(art: Zielwerteingabe['periodenart']): void {
    const j = eingabe.periode.slice(0, 4);
    setze({ periodenart: art, periode: art === 'jahr' ? j : art === 'quartal' ? `${j}-Q1` : `${j}-01` });
  }

  async function speichern(e: FormEvent): Promise<void> {
    e.preventDefault();
    try {
      const z = await zielwertSpeichern({ ...eingabe, wert: eingabe.wert.replace(',', '.'), toleranz: eingabe.toleranz.replace(',', '.') });
      setzeHinweis(`Ziel für ${name(z.kennzahl)}, ${periodeText(z)} gespeichert.`);
      setzeFehler(undefined);
      await laden();
    } catch (u) {
      if (u instanceof ApiFehler) setzeFehler(u);
      else throw u;
    }
  }

  /** Wirft (CRM-104): Der Fehler steht in der Rückfrage, und sie bleibt offen. */
  async function loeschen(z: Zielwert): Promise<void> {
    await zielwertLoeschen(z.id);
    setzeZuLoeschen(null);
    setzeHinweis(`Ziel für ${name(z.kennzahl)}, ${periodeText(z)} gelöscht.`);
    await laden();
  }

  return (
    <div className="zielwerte">
      {fehler !== undefined && <Meldung fehler={fehler} />}
      {hinweis !== undefined && (
        <p className="hinweiszeile" role="status">
          {hinweis}
        </p>
      )}

      {darfPflegen ? (
        <form className="zielwerte__formular" onSubmit={(e) => void speichern(e)} aria-label="Zielwert pflegen">
          <div className="feld">
            <label className="feld__beschriftung" htmlFor="ziel-kennzahl">
              Kennzahl
            </label>
            <select
              id="ziel-kennzahl"
              className="feld__eingabe"
              value={eingabe.kennzahl}
              onChange={(e) => setze({ kennzahl: e.target.value })}
            >
              {bereiteKennzahlen(katalog.kennzahlen).map((k) => (
                <option key={k.kennung} value={k.kennung}>
                  {k.name}
                </option>
              ))}
            </select>
          </div>
          <div className="feld">
            <label className="feld__beschriftung" htmlFor="ziel-art">
              Zeitraum
            </label>
            <select
              id="ziel-art"
              className="feld__eingabe"
              value={eingabe.periodenart}
              onChange={(e) => periodenart(e.target.value as Zielwerteingabe['periodenart'])}
            >
              {(['monat', 'quartal', 'jahr'] as const).map((a) => (
                <option key={a} value={a}>
                  {PERIODENNAMEN[a]}
                </option>
              ))}
            </select>
          </div>
          <div className="feld">
            <label className="feld__beschriftung" htmlFor="ziel-periode">
              {PERIODENNAMEN[eingabe.periodenart]}
            </label>
            {eingabe.periodenart === 'monat' ? (
              <input
                id="ziel-periode"
                className="feld__eingabe"
                type="month"
                value={eingabe.periode}
                onChange={(e) => e.target.value !== '' && setze({ periode: e.target.value })}
              />
            ) : eingabe.periodenart === 'quartal' ? (
              <select
                id="ziel-periode"
                className="feld__eingabe"
                value={eingabe.periode}
                onChange={(e) => setze({ periode: e.target.value })}
              >
                {[jahr - 1, jahr, jahr + 1].flatMap((j) =>
                  [1, 2, 3, 4].map((q) => (
                    <option key={`${String(j)}-Q${String(q)}`} value={`${String(j)}-Q${String(q)}`}>
                      Q{q} {j}
                    </option>
                  )),
                )}
              </select>
            ) : (
              <input
                id="ziel-periode"
                className="feld__eingabe"
                type="number"
                min={2000}
                max={2999}
                value={eingabe.periode}
                onChange={(e) => setze({ periode: e.target.value })}
              />
            )}
          </div>
          <div className="feld">
            <label className="feld__beschriftung" htmlFor="ziel-dimension">
              Für
            </label>
            <select
              id="ziel-dimension"
              className="feld__eingabe"
              value={eingabe.dimension}
              onChange={(e) => setze({ dimension: e.target.value as Zielwerteingabe['dimension'], dimensionswert: '' })}
            >
              {(['gesamt', 'betreuer', 'kundengruppe'] as const).map((d) => (
                <option key={d} value={d}>
                  {DIMENSIONSNAMEN[d]}
                </option>
              ))}
            </select>
          </div>
          {eingabe.dimension !== 'gesamt' && (
            <div className="feld">
              <label className="feld__beschriftung" htmlFor="ziel-dimensionswert">
                {DIMENSIONSNAMEN[eingabe.dimension]}
              </label>
              <select
                id="ziel-dimensionswert"
                className="feld__eingabe"
                value={eingabe.dimensionswert}
                onChange={(e) => setze({ dimensionswert: e.target.value })}
              >
                <option value="">— wählen —</option>
                {eingabe.dimension === 'betreuer'
                  ? merkmale.betreuer.map((b) => (
                      <option key={b.id} value={b.id}>
                        {b.name}
                      </option>
                    ))
                  : merkmale.kundengruppen.map((g) => (
                      <option key={g} value={g}>
                        {g}
                      </option>
                    ))}
              </select>
            </div>
          )}
          <div className="feld">
            <label className="feld__beschriftung" htmlFor="ziel-wert">
              Ziel ({einheitszeichen(einheit(eingabe.kennzahl))})
            </label>
            <input
              id="ziel-wert"
              className="feld__eingabe"
              inputMode="decimal"
              value={eingabe.wert}
              onChange={(e) => setze({ wert: e.target.value })}
            />
          </div>
          <div className="feld">
            <label className="feld__beschriftung" htmlFor="ziel-toleranz">
              Toleranz (% des Ziels, gelb)
            </label>
            <input
              id="ziel-toleranz"
              className="feld__eingabe"
              inputMode="decimal"
              value={eingabe.toleranz}
              onChange={(e) => setze({ toleranz: e.target.value })}
            />
          </div>
          <button type="submit" className="knopf knopf--haupt" disabled={eingabe.wert.trim() === ''}>
            Ziel speichern
          </button>
        </form>
      ) : (
        <p className="hilfe">Zielwerte pflegt die Geschäftsleitung.</p>
      )}

      {liste === undefined ? (
        <p className="hilfe" role="status">
          Zielwerte werden geladen …
        </p>
      ) : liste.length === 0 ? (
        <p className="hilfe">Noch kein Zielwert hinterlegt.</p>
      ) : (
        <div className="tabellenhuelle">
          <table className="liste__tabelle">
            <caption className="nur-vorlesen">Zielwerte</caption>
            <thead>
              <tr>
                <th scope="col">Kennzahl</th>
                <th scope="col">Zeitraum</th>
                <th scope="col">Für</th>
                <th scope="col" className="zahl">
                  Ziel
                </th>
                <th scope="col" className="zahl">
                  Toleranz
                </th>
                {darfPflegen && <th scope="col">Handlung</th>}
              </tr>
            </thead>
            <tbody>
              {liste.map((z) => (
                <tr key={z.id}>
                  <th scope="row">{name(z.kennzahl)}</th>
                  <td>
                    {PERIODENNAMEN[z.periodenart]} {periodeText(z)}
                  </td>
                  <td>{fuer(z)}</td>
                  <td className="zahl">{wertText(z.wert, einheit(z.kennzahl))}</td>
                  <td className="zahl">{z.toleranz.replace('.', ',')} %</td>
                  {darfPflegen && (
                    <td>
                      <button
                        type="button"
                        className="knopf knopf--eingriff knopf--klein"
                        aria-label={`Ziel ${name(z.kennzahl)} ${periodeText(z)} löschen …`}
                        onClick={() => {
                          setzeHinweis(undefined);
                          setzeZuLoeschen(z);
                        }}
                      >
                        Löschen …
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {zuLoeschen !== null && (
        <Rueckfrage
          frage="Zielwert löschen?"
          unterzeile={`${name(zuLoeschen.kennzahl)} · ${PERIODENNAMEN[zuLoeschen.periodenart]} ${periodeText(zuLoeschen)} · ${fuer(zuLoeschen)}`}
          tat="Löschen"
          beiBestaetigen={() => loeschen(zuLoeschen)}
          beiSchliessen={() => setzeZuLoeschen(null)}
        >
          <p>
            Das Ziel von {wertText(zuLoeschen.wert, einheit(zuLoeschen.kennzahl))} entfällt; Berichte und Widgets
            zeigen für diesen Zeitraum keine Ampel mehr. Ein neues Ziel lässt sich jederzeit wieder anlegen.
          </p>
        </Rueckfrage>
      )}
    </div>
  );
}
